import { useState } from "react";
import { put } from "@common/api/apiShared";
import { useOptimisticUpdate } from "@common/hooks/useOptimisticUpdate";
import { useResponsibleQuery } from "../../user/hooks/useResponsibleQuery";
import { UserSelectableItem } from "../../user/User.types";
import { ActivitySearchItem } from "./ActivitiesSearch.types";

export const responsibleFieldId = "responsible";

// Only one field is edited at a time, so entering edit mode never forces more than one column into the view.
// Adding a second editable field means adding a menu item for it, not widening this mode.
export type EditableActivityField = typeof responsibleFieldId;

export function showsResponsibleEditor(activity: ActivitySearchItem, canEditAnnualWheel: boolean, canEditContracts: boolean) {
    // A processing activity validation activity can never carry a responsible, and the guard throws from the domain
    // property setter, which surfaces as a 500 and navigates the whole app away.
    if (activity.isProcessingActivityValidation) return false;

    // A plan locked row is already covered by its own locked treatment and upgrade dialog.
    if (activity.notPartOfCurrentPlan) return false;

    return activity.isObligation ? canEditContracts : canEditAnnualWheel;
}

export function canEditResponsible(activity: ActivitySearchItem, canEditAnnualWheel: boolean, canEditContracts: boolean) {
    return showsResponsibleEditor(activity, canEditAnnualWheel, canEditContracts) && !activity.deleted;
}

export function withEditedColumn(displayFields: Array<string>, editingField: string | null, catalogueOrder: Array<string>) {
    if (!editingField || displayFields.includes(editingField)) return displayFields;

    // Inserted where the catalogue would have placed it, so a forced column does not simply appear at the far right.
    const editedPosition = catalogueOrder.indexOf(editingField);
    const insertAt = displayFields.findIndex((field) => catalogueOrder.indexOf(field) > editedPosition);

    return insertAt === -1 ? [...displayFields, editingField] : [...displayFields.slice(0, insertAt), editingField, ...displayFields.slice(insertAt)];
}

export interface UseActivitiesInlineEditProps {
    activities: Array<ActivitySearchItem> | undefined;
    displayFields: Array<string>;
    catalogueOrder: Array<string>;
    onResponsibleSaved: (activityId: string, responsible: UserSelectableItem) => Promise<void>;
    onEditModeClosed: () => void;
}

export function useActivitiesInlineEdit(props: UseActivitiesInlineEditProps) {
    const [editingField, setEditingField] = useState<EditableActivityField | null>(null);
    const [hasSavedChanges, setHasSavedChanges] = useState(false);
    const { addToQueueWithNoResponse } = useOptimisticUpdate();

    // Only fetched once the user actually starts editing, so opening the page costs nothing.
    const responsibleQuery = useResponsibleQuery(editingField === responsibleFieldId);

    // Seeded with the responsibles actually on the page so that a deactivated user still resolves to a name
    // instead of rendering an empty select.
    const selectableResponsibles = responsibleQuery.dataByList(
        (props.activities ?? []).map((activity) => activity.responsibleId).filter((id): id is string => id !== null)
    );

    async function onResponsibleChange(activityId: string, responsibleId: string) {
        const responsible = selectableResponsibles?.find((x) => x.id === responsibleId);
        if (!responsible) return;

        setHasSavedChanges(true);
        await props.onResponsibleSaved(activityId, responsible);

        // Queued per activity so two quick picks on the same row cannot land out of order.
        await addToQueueWithNoResponse(
            async (body: string) => {
                await put(`/annualWheelActivity/${activityId}/responsible`, body);
            },
            responsibleId,
            activityId
        );
    }

    function getResponsibleName(activity: ActivitySearchItem) {
        return selectableResponsibles?.find((x) => x.id === activity.responsibleId)?.name ?? "";
    }

    return {
        editingField,
        isEditMode: editingField !== null,
        editResponsible: () => setEditingField(responsibleFieldId),
        // Closing is the point where the list reconciles with the server. Until then rows keep their position so that
        // nothing moves under the cursor mid edit, which leaves the list knowingly out of sync with its own filter,
        // sort and count. Refetching here is what makes that state temporary rather than a lie left on screen.
        exitEditMode: () => {
            setEditingField(null);
            if (hasSavedChanges) {
                setHasSavedChanges(false);
                props.onEditModeClosed();
            }
        },
        effectiveDisplayFields: withEditedColumn(props.displayFields, editingField, props.catalogueOrder),
        selectableResponsibles,
        responsiblesLoading: responsibleQuery.isLoading,
        getResponsibleName,
        onResponsibleChange,
    };
}
